import { Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { MessagesDrawer } from "@/components/MessagesDrawer";
import { MessagesListDrawer } from "@/components/MessagesListDrawer";
import { DirectProgramMessageDrawer } from "@/components/DirectProgramMessageDrawer";
import { DirectPabMessageDrawer } from "@/components/DirectPabMessageDrawer";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/utils/roleUtils";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { useState } from "react";
import { DrawerProvider, useDrawer } from "@/context/DrawerContext";
import { PabSessionProvider } from "@/context/PabSessionContext";
import { UseMultiHubDrawer } from "@/hooks/useMultiHubDrawer";
import { UseAdvisorGoalsDrawer } from "@/hooks/useAdvisorGoalsDrawerGlobal";
import { UseSessionPrepDrawer } from "@/hooks/useSessionPrepDrawerGlobal";
import { UseAchieverSessionDrawers } from "@/hooks/useAchieverSessionDrawersGlobal";
import { useAdvisorPABs } from "@/hooks/useDashboardStats";
import { useExpertPrograms } from "@/hooks/useExpertPrograms";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import { NavigationSidebar } from "@/pages/navigationSidebar";
import { UserRole } from "../pages/sidebar.config";
import { OnboardingGuard } from "@/components/OnboardingGuard";
import { isPublishedProgramStatus } from "@/utils/programStatus";
const MainLayoutContent = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const {
    openDrawerWithMessages,
    openDrawerWithSessions,
    isMessagesListDrawerOpen,
    openMessagesListDrawer,
    closeMessagesListDrawer,
    isMessagesDrawerOpen,
    messagesDrawerSessionId,
    messagesDrawerSelectedAdvisorId,
    messagesDrawerProgramExternalId,
    messagesDrawerChatTitle,
    messagesDrawerChatSubtitle,
    messagesDrawerGroupThread,
    closeMessagesDrawer,
    isDirectProgramMessageDrawerOpen,
    directProgramMessageDrawerProgramId,
    directProgramMessageDrawerProgramName,
    directProgramMessageDrawerRecipients,
    closeDirectProgramMessageDrawer,
    isDirectPabMessageDrawerOpen,
    directPabMessageDrawerSessionId,
    directPabMessageDrawerSessionLabel,
    directPabMessageDrawerRecipients,
    directPabMessageDrawerInitialMessage,
    closeDirectPabMessageDrawer,
  } = useDrawer();

  // Close mobile nav when route changes (e.g. after clicking a nav item)
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // Check if advisor has PABs or active programs to conditionally show Messages icon
  const { data: advisorPabs } = useAdvisorPABs({ enabled: hasRole(user, "advisor") });
  const hasAdvisorPabs = Array.isArray(advisorPabs) && advisorPabs.length > 0;
  const isAdvisor = hasRole(user, "advisor");
  const isAchiever = hasRole(user, "achiever");

  const { advisorPrograms } = useExpertPrograms();
  const hasActiveAdvisorProgram = isAdvisor && advisorPrograms.some((p) => isPublishedProgramStatus(p.status));

  const showMessages = isAchiever || (isAdvisor && (hasAdvisorPabs || hasActiveAdvisorProgram));

  const onMessagesClick = showMessages ? openDrawerWithMessages : undefined;

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleHamburgerClick = () => {
    setIsMobileNavOpen(true);
  };

  // Dual-role support: which workspace the sidebar reflects, not just which
  // roles the user holds.
  const activeWorkspace = useActiveWorkspace();
  const sidebarRole = (): UserRole => activeWorkspace;

  const renderSidebar = () => {
    if (!user) return null;
    return <NavigationSidebar role={sidebarRole()} isCollapsed={isSidebarCollapsed} />;
  };

  return (
    <div className="flex h-full flex-col bg-theme-bg-page">
      {/* Environment Badge */}
      <EnvironmentBadge />

      {/* Header */}
      <Header
        onToggleSidebar={handleToggleSidebar}
        onHamburgerClick={handleHamburgerClick}
        onMessagesClick={onMessagesClick}
        onCalendarClick={openDrawerWithSessions}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      {/* Main Content */}
      <main className="flex h-[calc(100vh-var(--layout-top-header-height))]">
        {/* Desktop sidebar - hidden on mobile */}
        <div className="hidden md:flex md:flex-shrink-0">{renderSidebar()}</div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile nav overlay - sidebar slides in from left */}
      {user && isMobileNavOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setIsMobileNavOpen(false)}
            aria-hidden
          />
          {/* w-60 matches the sidebar's own width, so the drawer has no dead
              strip to the right of the nav. */}
          <div className="fixed inset-y-0 left-0 z-50 w-60 max-w-[85vw] bg-theme-bg-surface border-r border-theme-border shadow-xl md:hidden transition-transform">
            <NavigationSidebar role={sidebarRole()} isCollapsed={false} />
          </div>
        </>
      )}

      {/* MessagesListDrawer — inbox/overview opened from header Messages icon */}
      {(isAchiever || isAdvisor) && (
        <MessagesListDrawer
          isOpen={isMessagesListDrawerOpen}
          onClose={closeMessagesListDrawer}
        />
      )}

      {/* MessagesDrawer — group/1:1 chat opened from list drawer */}
      {(isAchiever || isAdvisor) && (
        <MessagesDrawer
          isOpen={isMessagesDrawerOpen}
          onClose={closeMessagesDrawer}
          onBack={() => { closeMessagesDrawer(); openMessagesListDrawer(); }}
          sessionId={messagesDrawerSessionId ?? undefined}
          initialSelectedAdvisorId={messagesDrawerSelectedAdvisorId}
          initialProgramExternalId={messagesDrawerProgramExternalId ?? undefined}
          chatTitle={messagesDrawerChatTitle ?? undefined}
          chatSubtitle={messagesDrawerChatSubtitle ?? undefined}
          openGroupThread={messagesDrawerGroupThread}
        />
      )}

      {/* DirectProgramMessageDrawer — global 1:1 DM for program members */}
      {(isAchiever || isAdvisor) && isDirectProgramMessageDrawerOpen && directProgramMessageDrawerProgramId && (
        <DirectProgramMessageDrawer
          isOpen={isDirectProgramMessageDrawerOpen}
          onClose={closeDirectProgramMessageDrawer}
          onBack={() => { closeDirectProgramMessageDrawer(); openMessagesListDrawer(); }}
          programId={directProgramMessageDrawerProgramId}
          programName={directProgramMessageDrawerProgramName ?? undefined}
          recipients={directProgramMessageDrawerRecipients}
        />
      )}

      {/* DirectPabMessageDrawer — global 1:1 DM for PAB session members */}
      {(isAchiever || isAdvisor) && isDirectPabMessageDrawerOpen && directPabMessageDrawerSessionId && (
        <DirectPabMessageDrawer
          isOpen={isDirectPabMessageDrawerOpen}
          onClose={closeDirectPabMessageDrawer}
          onBack={() => { closeDirectPabMessageDrawer(); openMessagesListDrawer(); }}
          sessionId={directPabMessageDrawerSessionId}
          sessionLabel={directPabMessageDrawerSessionLabel ?? undefined}
          recipients={directPabMessageDrawerRecipients}
          initialMessage={directPabMessageDrawerInitialMessage ?? undefined}
        />
      )}

      {/* Global MultiHubDrawer - accessible from all pages (for achiever flow) */}
      {isAchiever && <UseMultiHubDrawer />}
      
      {/* Global AdvisorGoalsDrawer - accessible from all pages (for advisor flow) - Goals tab only */}
      {isAdvisor && <UseAdvisorGoalsDrawer />}

      {/* Global SessionPrepDrawer - accessible from all pages (for advisor flow) - opened from notifications */}
      {isAdvisor && <UseSessionPrepDrawer />}

      {/* Global Session Prep / Session Summary drawers (achiever flow) - opened from notifications */}
      {isAchiever && <UseAchieverSessionDrawers />}
    </div>
  );
};

export const MainLayout = () => {
  return (
    <DrawerProvider>
      <PabSessionProvider>
        <OnboardingGuard>
          <MainLayoutContent />
        </OnboardingGuard>
      </PabSessionProvider>
    </DrawerProvider>
  );
};
